import { create } from 'zustand';
import { persist, createJSONStorage, subscribeWithSelector, devtools } from 'zustand/middleware';
import type { User, TokenMetadata } from '../types/models/User';
import axiosInstance from '../api/axiosInstance';
import { runPreLogoutTasks } from './preLogoutTasks';
import { requestPeerPreLogoutRelease } from './preLogoutPeers';

// Helper to safely check if we're in development mode
// Note: process.env.NODE_ENV is injected by webpack DefinePlugin at build time
const isDevelopment = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - process is defined by webpack DefinePlugin at build time
    return process.env.NODE_ENV !== 'production';
  } catch {
    return true; // Default to development if process is not defined
  }
};

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  setUser: (user: User | null) => void;
  logout: () => Promise<void>;
  loadUserFromToken: () => boolean;
  clearAuthState: (options?: { markLoggingOut?: boolean }) => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          user: null,
          isLoading: true,
          isAuthenticated: false,

          setUser: (user) => {
            set(
              {
                user,
                isAuthenticated: !!user,
                isLoading: false,
              },
              false,
              'setUser'
            );
            // User is persisted automatically by zustand persist middleware (helios-auth-storage)
          },

          loadUserFromToken: () => {
            try {
              const tokenMetadata = localStorage.getItem('token_metadata');
              if (!tokenMetadata) {
                set(
                  { user: null, isLoading: false, isAuthenticated: false },
                  false,
                  'loadUserFromToken/noToken'
                );
                return false;
              }

              const metadata: TokenMetadata = JSON.parse(tokenMetadata);
              const expiresAt = new Date(metadata.expiresAt);

              if (expiresAt <= new Date()) {
                console.warn('Token expired, clearing authentication state');
                // Stale-session cleanup, NOT an explicit logout: don't raise the
                // __heliosLoggingOut flag. There is no page reload on this path
                // (ProtectedRoute does an SPA Navigate to /login), so the flag
                // would poison the whole window session and block ProtectedRoute
                // from saving helios-post-login-redirect for the deep link.
                get().clearAuthState({ markLoggingOut: false });
                set({ isLoading: false }, false, 'loadUserFromToken/tokenExpired');
                return false;
              }

              // User is already hydrated by zustand persist middleware (helios-auth-storage)
              const user = get().user;
              if (user) {
                set(
                  {
                    isLoading: false,
                    isAuthenticated: true,
                  },
                  false,
                  'loadUserFromToken/success'
                );
                return true;
              }

              set(
                { user: null, isLoading: false, isAuthenticated: false },
                false,
                'loadUserFromToken/noUserData'
              );
              return false;
            } catch (error) {
              console.error('Error loading user from token:', error);
              // Same as the expired-token branch: cleanup, not a logout.
              get().clearAuthState({ markLoggingOut: false });
              set({ isLoading: false }, false, 'loadUserFromToken/error');
              return false;
            }
          },

          clearAuthState: ({ markLoggingOut = true } = {}) => {
            // Signal that a logout is in flight. ProtectedRoute, axios 401 handler,
            // and custom-fetch 401 handler check this flag and skip writing
            // helios-post-login-redirect, so we don't repopulate localStorage
            // between this call and the page reload. The flag dies with the
            // window object on the next full page load — so it MUST only be set
            // on paths that end in one (explicit logout, cross-tab logout).
            // Boot-time cleanup of a stale/expired session passes
            // markLoggingOut: false because it continues as SPA navigation and
            // must keep the post-login deep-link redirect working.
            if (markLoggingOut) {
              (window as unknown as { __heliosLoggingOut?: boolean }).__heliosLoggingOut = true;
            }

            // Preserve UX preferences that are unrelated to identity — everything
            // else goes. We deliberately drop helios-post-login-redirect on explicit
            // logout (it's only meaningful mid-OAuth flow, not after the user signs
            // out).
            //
            // A key may only be added here if it holds NO user-specific data, or is
            // internally namespaced by user id. helios-reading-width (Story 163.5)
            // qualifies via the second route: it nests its per-document choices under
            // the signed-in user's id, so surviving logout cannot leak one user's
            // choices to the next person signing in on this browser.
            const preservedKeys = ['helios-theme', 'helios-reading-width'];
            const preserved = preservedKeys.map((key) => [key, localStorage.getItem(key)] as const);

            localStorage.clear();
            sessionStorage.clear();

            preserved.forEach(([key, value]) => {
              if (value !== null) {
                localStorage.setItem(key, value);
              }
            });

            // Clear all non-HttpOnly cookies. HttpOnly cookies (access_token,
            // refresh_token, XSRF) can only be cleared by the server, which
            // /api/auth/logout does via Response.Cookies.Delete.
            document.cookie.split(';').forEach((cookie) => {
              const name = cookie.split('=')[0].trim();
              if (name) {
                document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`;
              }
            });
          },

          logout: async () => {
            // BUG-146069 — hand back server-side resources held by open
            // surfaces (the steering editor's exclusive document lock) FIRST, while
            // this browser still has valid credentials. Everything below invalidates
            // them: the POST clears the auth cookies server-side, `clearAuthState()`
            // wipes storage, and the `auth:logout` listener navigates away
            // synchronously. A release attempted after any of that either 401s or is
            // aborted mid-flight, which is exactly how a signed-out user used to leave
            // a document locked for everyone else until the idle sweep reaped it.
            //
            // The peer request covers what OTHER TABS hold: their `LOGOUT` handler
            // only clears state and navigates, and the auth cookies are one shared
            // jar, so a tab told after the fact could no longer release anything —
            // it has to be asked now, while the whole browser is still authenticated.
            //
            // Concurrent, not sequential: both halves are separately bounded, so
            // sign-out waits for the slower one rather than for their sum.
            //
            // Both are bounded and non-throwing by contract, and allSettled keeps
            // that true here even if a future edit inside either one breaks it: a
            // slow or failing release delays sign-out briefly, but nothing on this
            // path may leave a user who asked to leave still signed in.
            // BUG-146069-002 — `allSettled` discards rejections by design, which was the fourth
            // and outermost swallow on the release path. Both calls are written not to reject, so
            // a rejection here means one of them broke its own contract: worth a line rather than
            // nothing. Sign-out still proceeds regardless.
            const released = await Promise.allSettled([
              runPreLogoutTasks(),
              requestPeerPreLogoutRelease(),
            ]);
            released.forEach((r, i) => {
              if (r.status === 'rejected') {
                console.error(
                  `[BUG-146069] ${i === 0 ? 'runPreLogoutTasks' : 'requestPeerPreLogoutRelease'} ` +
                    'rejected, which it is written never to do:',
                  r.reason
                );
              }
            });

            try {
              await axiosInstance.post('/api/auth/logout');
            } catch (error) {
              console.error('Logout error:', error);
            } finally {
              // Null the in-memory state FIRST so Zustand's persist middleware
              // doesn't immediately re-write helios-auth-storage after we clear it.
              set({ user: null, isAuthenticated: false }, false, 'logout');
              get().clearAuthState();
              // Belt-and-braces: nuke the persisted store entry explicitly in
              // case any other set() fires between here and the page reload.
              localStorage.removeItem('helios-auth-storage');

              // Broadcast logout event via CustomEvent for navigation
              // BroadcastChannel sync is handled by initializeAuthSync (authSync.ts)
              window.dispatchEvent(new CustomEvent('auth:logout'));
            }
          },
        }),
        {
          name: 'helios-auth-storage',
          // Wrap localStorage so persist refuses to write a null-user payload.
          // Without this, every store init on a logged-out page (e.g. /login after
          // logout) immediately writes `{"state":{"user":null},"version":1}` back
          // to localStorage, leaving a stale-looking helios-* key behind.
          storage: createJSONStorage(() => ({
            getItem: (key) => localStorage.getItem(key),
            setItem: (key, value) => {
              try {
                const parsed = JSON.parse(value);
                if (parsed?.state?.user == null) {
                  localStorage.removeItem(key);
                  return;
                }
              } catch {
                // fall through to a normal write if value isn't JSON we recognize
              }
              localStorage.setItem(key, value);
            },
            removeItem: (key) => localStorage.removeItem(key),
          })),
          version: 1,
          migrate: (persistedState) => {
            // v0→v1: roles removed from User, now come from /me via userProfileStore
            const state = persistedState as { user?: Record<string, unknown> };
            if (state.user) {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { roles, ...cleanUser } = state.user;
              state.user = cleanUser;
            }
            return persistedState as AuthState;
          },
          // Explicitly pick only User fields to prevent stale properties from persisting
          partialize: (state) => ({
            user: state.user
              ? {
                  id: state.user.id,
                  email: state.user.email,
                  displayName: state.user.displayName,
                  tenantId: state.user.tenantId,
                  userType: state.user.userType,
                  ...(state.user.avatarUrl && { avatarUrl: state.user.avatarUrl }),
                  ...(state.user.entraObjectId && { entraObjectId: state.user.entraObjectId }),
                  ...(state.user.entraExternalIdObjectId && {
                    entraExternalIdObjectId: state.user.entraExternalIdObjectId,
                  }),
                }
              : null,
          }),
        }
      )
    ),
    {
      name: 'Helios Auth Store',
      // Enable devtools in development, disable in production
      enabled: isDevelopment(),
    }
  )
);
